import TriangleExclamationIcon from "@/svg/triangle-exclamation-solid.svg?react";
import { createRequiredContext } from "@enymo/react-better-context";
import { assertNotNull } from "@enymo/ts-nullsafe";
import { AxiosError } from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useParams } from "react-router";
import route from "ziggy-js";
import LanguageSelect from "../Components/LanguageSelect";
import { useApp } from "../Providers/AppProvider";
import { Language, languages } from "../common";
import { User } from "../resources";
import { SocialAccountType } from "../types";

export interface ConnectorLink {
    sid: string,
    user: Pick<User, "id" | "has_avatar" | "first_name" | "last_name" | "avatar_version">,
    accounts: {
        type: Exclude<SocialAccountType, "instagram">,
        id: number | null,
        connected: boolean
    }[]
}

const [Provider, useConnectorLink] = createRequiredContext<{
    connectorLink: ConnectorLink,
    refresh: () => Promise<void>
}>("Connector Layout must be present in component tree");

export { useConnectorLink };
export default function Connector() {
    const {t, i18n} = useTranslation();
    const {sid} = useParams();
    assertNotNull(sid, "sid must be set");
    const [connectorLink, setConnectorLink] = useState<ConnectorLink | null>();
    const {axios} = useApp();

    const refresh = useCallback(async () => {
        try {
            const response = await axios.get(route("connector-links.show", {sid}));
            setConnectorLink(response.data);
        }
        catch (e) {
            if (e instanceof AxiosError && e.response?.status === 404) {
                setConnectorLink(null);
            }
            else {
                throw e;
            }
        }
    }, [sid, setConnectorLink]);

    useEffect(() => void refresh(), [refresh]);

    const handleChangeLanguage = useCallback((language: Language) => {
        i18n.changeLanguage(language);
    }, [i18n]);

    return (
        <div className="grow bg-linear-to-b from-[#EEF1F8] to-bg-300 flex flex-col px-4 py-6 gap-6">
            <main className="grow flex justify-center items-center">
                {connectorLink === null ? (
                    <div className="rounded-xl shadow-card bg-bg-100 px-6 py-10 max-w-[500px] w-full flex flex-col items-center">
                        <TriangleExclamationIcon className="fill-error-500 w-15" />
                        <h1 className="heading-xl mt-10 text-center">{t("connector.linkExpired.title")}</h1>
                        <p className="body-m mt-6 text-center">{t("connector.linkExpired.text")}</p>
                    </div>
                ) : connectorLink !== undefined && (
                    <Provider value={{connectorLink, refresh}}>
                        <Outlet />
                    </Provider>
                )}
            </main>
            <div className="flex flex-col items-center gap-2.5">
                <LanguageSelect languages={languages} value={i18n.language} onChange={handleChangeLanguage} />
                <span className="text-xs text-text-300">{t("copyright", {currentYear: new Date().getFullYear()})}</span>
            </div>
        </div>
    )
}